import React, { useState } from 'react'
import { Switch } from 'components/UI/Switch/Switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/UI/Modal'
import { Calendar } from 'components/UI/calendar'
import { Settings, Trash2, Edit, Star, DollarSign } from 'lucide-react'
import { Select } from 'components/UI/Select/Select'
import type { ISelectItem } from 'components/UI/Select/Select'
import { Dropdown } from 'components/UI/Dropdown/Dropdown'
import { Combobox } from 'components/UI/combobox/combobox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/UI/command'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { Anchor, RouterLink } from 'components/UI/Anchor'
import { Attachment } from 'components/UI/Attachment/Attachment'
import { InfoCard } from 'components/UI/InfoCard'
import { VariantSelectButton } from 'components/UI/VariantSelectButton'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from 'components/UI/Carousel/Carousel'
import { ErrorBoundary } from 'components/UI/ErrorBoundary'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import FbButton from 'components/FbUI/FbButton'
import { Input } from 'components/FormUtilsV2/Input/input'
import { Label } from 'components/FormUtilsV2/Label/label'
import { Preview, Section, Row, Col } from './PreviewKit'

// ---------------------------------------------------------------------------
// Switch
// ---------------------------------------------------------------------------
function SwitchPreview(): React.ReactElement {
  const [a, setA] = useState(false)
  const [b, setB] = useState(true)
  return (
    <Preview>
      <Section label="Basic">
        <Row label="No label — controlled">
          <Switch checked={a} onChange={(e) => setA(e.target.checked)} />
          <span className="text-sm text-fg-secondary">{a ? 'On' : 'Off'}</span>
        </Row>
      </Section>
      <Section label="With labels">
        <Col>
          <Switch
            checked={b}
            onChange={(e) => setB(e.target.checked)}
            labelLeft="Inactive"
            labelRight="Active"
          />
          <Switch checked={false} readOnly labelLeft="Off" labelRight="On" />
        </Col>
      </Section>
      <Section label="States">
        <Row>
          <Switch checked={true} disabled readOnly />
          <span className="text-xs text-fg-secondary">Disabled on</span>
          <Switch checked={false} disabled readOnly />
          <span className="text-xs text-fg-secondary">Disabled off</span>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------
function CalendarPreview(): React.ReactElement {
  const [date, setDate] = useState<Date | undefined>(new Date())
  return (
    <Preview>
      <Section label="Single-date mode">
        <div className="overflow-hidden max-w-full">
          <Calendar mode="single" selected={date} onSelect={setDate} />
        </div>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Modal (Radix Dialog)
// ---------------------------------------------------------------------------
function ModalPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Basic dialog">
        <Row>
          <Dialog>
            <DialogTrigger asChild>
              <FbButton variant="outline" size="sm">
                Open Dialog
              </FbButton>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Example Dialog</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-fg-secondary">
                This is the Radix Dialog primitive. It handles focus trapping,
                keyboard dismiss (Esc), and accessible overlay automatically.
              </p>
            </DialogContent>
          </Dialog>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Select
// ---------------------------------------------------------------------------
const statusItems: ISelectItem<string>[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Pending', value: 'pending' },
  { label: 'Archived', value: 'archived' },
]

function SelectPreview(): React.ReactElement {
  const [selected, setSelected] = useState<string>('active')
  return (
    <Preview>
      <Section label="Secondary button trigger (default)">
        <Row>
          <Select
            items={statusItems}
            selected={selected}
            onSelect={(v) => setSelected(v)}
            buttonLabel="Status"
            buttonVariant="secondary"
          />
        </Row>
      </Section>
      <Section label="Search enabled">
        <Row>
          <Select
            items={statusItems}
            selected={selected}
            onSelect={(v) => setSelected(v)}
            buttonLabel="Status"
            buttonVariant="secondary"
            searchPlaceholder="Filter status..."
          />
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// BaseDropdown (Radix DropdownMenu)
// ---------------------------------------------------------------------------
function BaseDropdownPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Full menu with groups, separators, icons">
        <Row>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <FbButton variant="secondary" size="sm">
                Actions ▾
              </FbButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>File</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem>
                  <Edit size={14} className="mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Settings size={14} className="mr-2" /> Settings
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive">
                <Trash2 size={14} className="mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Dropdown (legacy wrapper)
// ---------------------------------------------------------------------------
function DropdownPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="buttonComponent + items array">
        <Row>
          <Dropdown
            buttonComponent={
              <FbButton variant="secondary" size="sm">
                Options ▾
              </FbButton>
            }
            items={[
              { label: 'Edit', icon: <Edit size={14} />, callback: () => {} },
              {
                label: 'Settings',
                icon: <Settings size={14} />,
                callback: () => {},
              },
              {
                label: 'Delete (disabled)',
                icon: <Trash2 size={14} />,
                disabled: true,
              },
            ]}
          />
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Combobox
// ---------------------------------------------------------------------------
const comboboxOptions = [
  { label: 'New York', value: 'ny' },
  { label: 'Los Angeles', value: 'la' },
  { label: 'Chicago', value: 'chi' },
  { label: 'Houston', value: 'hou' },
  { label: 'Phoenix', value: 'phx' },
]

function ComboboxPreview(): React.ReactElement {
  const [selected, setSelected] = useState<string | number | undefined>()
  return (
    <Preview>
      <Section label="Searchable select (Combobox)">
        <Row>
          <Combobox
            placeholder="Select a city..."
            searchPlaceholder="Search cities..."
            options={comboboxOptions}
            selected={selected}
            onSelect={setSelected}
            notFoundPlaceholder="No cities found"
          />
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Command
// ---------------------------------------------------------------------------
function CommandPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Command palette with search + empty state">
        <div className="border border-line rounded-md w-64">
          <Command>
            <CommandInput placeholder="Search commands..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup heading="Actions">
                <CommandItem>
                  <Edit size={14} className="mr-2" />
                  Edit record
                </CommandItem>
                <CommandItem>
                  <Settings size={14} className="mr-2" />
                  Preferences
                </CommandItem>
                <CommandItem>
                  <Star size={14} className="mr-2" />
                  Favourite
                </CommandItem>
                <CommandItem className="text-destructive">
                  <Trash2 size={14} className="mr-2" />
                  Delete
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Popover
// ---------------------------------------------------------------------------
function PopoverPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Trigger → anchored panel">
        <Row>
          <Popover>
            <PopoverTrigger asChild>
              <FbButton variant="outline" size="sm">
                Open Popover
              </FbButton>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-3">
              <p className="text-sm font-medium mb-1">Quick actions</p>
              <p className="text-xs text-fg-secondary">
                Popover positions relative to its trigger and traps interaction.
              </p>
            </PopoverContent>
          </Popover>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Anchor + RouterLink
// ---------------------------------------------------------------------------
function AnchorPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Anchor — external href">
        <Col>
          <Anchor href="https://example.com" target="_blank" rel="noreferrer">
            External link (opens new tab)
          </Anchor>
          <Anchor href="mailto:hello@example.com">Mailto link</Anchor>
        </Col>
      </Section>
      <Section label="RouterLink — internal React Router navigation">
        <Col>
          <RouterLink to="/component-library">
            Component Library (/component-library)
          </RouterLink>
          <RouterLink to="/dashboard">Dashboard (/dashboard)</RouterLink>
        </Col>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Attachment
// ---------------------------------------------------------------------------
function AttachmentPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="File types">
        <Col>
          <Attachment
            filename="proposal.pdf"
            size={204800}
            contentType="application/pdf"
            onClick={() => {}}
          />
          <Attachment
            filename="hero-image.png"
            size={51200}
            contentType="image/png"
            onClick={() => {}}
          />
          <Attachment
            filename="report.xlsx"
            size={92160}
            contentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onClick={() => {}}
          />
        </Col>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// InfoCard
// ---------------------------------------------------------------------------
function InfoCardPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="With icon + tooltip">
        <Row align="start">
          <InfoCard
            title="Revenue"
            value="$42,000"
            tooltip="Estimated annual revenue"
            icon={DollarSign}
          />
          <InfoCard
            title="Deals"
            value={28}
            tooltip="Open deals this quarter"
          />
        </Row>
      </Section>
      <Section label="Without icon or tooltip">
        <Row align="start">
          <InfoCard title="Locations" value={145} />
          <InfoCard title="Win Rate" value="68%" />
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// VariantSelectButton
// ---------------------------------------------------------------------------
function VariantSelectButtonPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="States">
        <Col>
          <VariantSelectButton text="Select Variant(s)" onClick={() => {}} />
          <VariantSelectButton
            text="Select Variant(s) — disabled"
            onClick={() => {}}
            disabled
          />
        </Col>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Carousel
// ---------------------------------------------------------------------------
const carouselSlides = [
  { label: 'Slide 1', color: 'bg-blue-100 text-blue-800' },
  { label: 'Slide 2', color: 'bg-green-100 text-green-800' },
  { label: 'Slide 3', color: 'bg-purple-100 text-purple-800' },
]

function CarouselPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Horizontal carousel with prev / next controls">
        <Carousel className="w-full max-w-xs mx-auto">
          <CarouselContent>
            {carouselSlides.map((slide) => (
              <CarouselItem key={slide.label}>
                <div
                  className={`h-24 rounded-lg flex items-center justify-center font-medium text-sm ${slide.color}`}
                >
                  {slide.label}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// ErrorBoundary
// ---------------------------------------------------------------------------
function ThrowingChild(): React.ReactElement {
  throw new Error('Intentional render error for demo')
}

function ErrorBoundaryPreview(): React.ReactElement {
  const [showError, setShowError] = useState(false)
  return (
    <Preview>
      <Section label="Wraps children — shows fallback on throw">
        <ErrorBoundary>
          <div className="text-sm text-fg-secondary p-3 border border-line rounded">
            Healthy content — no error thrown.
          </div>
        </ErrorBoundary>
      </Section>
      <Section label="Error state (click to trigger)">
        <ErrorBoundary key={showError ? 'err' : 'ok'}>
          {showError ? (
            <ThrowingChild />
          ) : (
            <button
              className="text-sm text-brand underline"
              onClick={() => setShowError(true)}
            >
              Click to throw inside ErrorBoundary →
            </button>
          )}
        </ErrorBoundary>
        {showError && (
          <button
            className="text-xxs text-fg-secondary underline mt-1"
            onClick={() => setShowError(false)}
          >
            Reset
          </button>
        )}
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Input (FormUtilsV2)
// ---------------------------------------------------------------------------
function InputPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="States">
        <Col>
          <Input placeholder="Placeholder text" />
          <Input defaultValue="Pre-filled value" />
          <Input disabled placeholder="Disabled input" />
          <Input type="number" placeholder="Numeric input" />
        </Col>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Label (FormUtilsV2)
// ---------------------------------------------------------------------------
function LabelPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Standalone">
        <Col>
          <Label>Standard label</Label>
          <Label className="text-fg-secondary font-normal">
            Muted / optional
          </Label>
        </Col>
      </Section>
      <Section label="Paired with Input">
        <Col>
          <div className="flex flex-col gap-1.5 w-64">
            <Label htmlFor="demo-label-input">Variant Name</Label>
            <Input id="demo-label-input" placeholder="e.g. 12oz Can" />
          </div>
          <div className="flex flex-col gap-1.5 w-64">
            <Label htmlFor="demo-label-disabled">Read-only field</Label>
            <Input
              id="demo-label-disabled"
              disabled
              defaultValue="Read-only value"
            />
          </div>
        </Col>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Export map
// ---------------------------------------------------------------------------
export const tier1Previews: Record<string, () => React.ReactNode> = {
  Switch: SwitchPreview,
  Input: InputPreview,
  Label: LabelPreview,
  calendar: CalendarPreview,
  Modal: ModalPreview,
  Select: SelectPreview,
  BaseDropdown: BaseDropdownPreview,
  Dropdown: DropdownPreview,
  combobox: ComboboxPreview,
  command: CommandPreview,
  popover: PopoverPreview,
  Anchor: AnchorPreview,
  Attachment: AttachmentPreview,
  InfoCard: InfoCardPreview,
  VariantSelectButton: VariantSelectButtonPreview,
  Carousel: CarouselPreview,
  ErrorBoundary: ErrorBoundaryPreview,
}
